import React, { useState, useEffect } from 'react';
import ProductForm from './components/forms/ProductForm';
import ServiceForm from './components/forms/ServiceForm';
import ReferenceForm from './components/forms/ReferenceForm';
import NewsForm from './components/forms/NewsForm';
import GalleryForm from './components/forms/GalleryForm';
import DocumentForm from './components/forms/DocumentForm';
import ContentManager from './components/admin/ContentManager';
import LoginForm from './components/auth/LoginForm';
import { Plus, List, LogOut, User, Clock, Home } from 'lucide-react';
import { 
  isAuthenticated, 
  getStoredUser, 
  logout, 
  verifyToken,
  getTimeUntilExpiry 
} from './services/auth-frontend.js';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [activeSubTab, setActiveSubTab] = useState('add'); // 'add' or 'manage'
  const [message, setMessage] = useState('');
  
  // Authentication state
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Message handlers for form components
  const handleSuccess = (message) => {
    setMessage(message);
  };

  const handleError = (message) => {
    setMessage(message);
  };

  // Clear message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated()) {
        // Verify token with backend
        const verification = await verifyToken();
        if (verification.valid) {
          setAuthenticated(true);
          setUser(verification.user);
        } else {
          setAuthenticated(false);
          setUser(null);
        }
      } else {
        setAuthenticated(false);
        setUser(null);
      }
      setAuthLoading(false);
    };

    checkAuth();
  }, []);

  // Handle successful login
  const handleLoginSuccess = (userData) => {
    setAuthenticated(true);
    setUser(userData);
    setMessage('✅ Başarıyla giriş yapıldı!');
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    setAuthenticated(false);
    setUser(null);
    setMessage('✅ Güvenli bir şekilde çıkış yapıldı');
  };

  const tabs = [
    { id: 'products', label: '📦 Ürünler', icon: '📦' },
    { id: 'services', label: '🔧 Hizmetler', icon: '🔧' },
    { id: 'references', label: '🏢 Referanslar', icon: '🏢' },
    { id: 'news', label: '📰 Haberler', icon: '📰' },
    { id: 'gallery', label: '🖼️ Galeri', icon: '🖼️' },
    { id: 'documents', label: '📄 Belgelerimiz', icon: '📄' }
  ];

  const subTabs = [
    { id: 'add', label: 'Yeni Ekle', icon: Plus },
    { id: 'manage', label: 'Mevcut İçerikler', icon: List }
  ];

  // Show loading spinner while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yetkilendirme kontrol ediliyor...</p>
        </div>
      </div>
    );
  }

  // Show login form if not authenticated
  if (!authenticated) {
    return <LoginForm onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              {/* Ana Sayfa Dönüş Butonu */}
              <a
                href="/"
                className="flex items-center space-x-2 px-4 py-2 bg-brand-orange hover:bg-orange-600 text-white rounded-lg transition-colors shadow-md hover:shadow-lg"
                title="Ana Sayfaya Dön"
              >
                <Home className="w-5 h-5" />
                <span className="font-medium">Ana Sayfa</span>
              </a>
              
              <div>
                <h1 className="text-2xl font-bold text-gray-900">🔧 Admin Panel</h1>
                <p className="text-gray-600 mt-1">Onur Beton İçerik Yönetimi</p>
              </div>
            </div>
            
            {/* User Info & Logout */}
            <div className="flex items-center space-x-4">
              {/* User Info */}
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <User className="w-4 h-4" />
                <span className="font-medium">{user?.username}</span>
              </div>
              
              {/* Session Time */}
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Clock className="w-4 h-4" />
                <span>{getTimeUntilExpiry()}</span>
              </div>
              
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Çıkış Yap"
              >
                <LogOut className="w-4 h-4" />
                <span>Çıkış</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Status Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.includes('✅') ? 'bg-green-100 text-green-800' : 
            message.includes('❌') ? 'bg-red-100 text-red-800' : 
            'bg-blue-100 text-blue-800'
          }`}>
            {message}
          </div>
        )}

        {/* Main Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setActiveSubTab('add'); // Reset to add tab when switching main tabs
                  }}
                  className={`py-4 px-2 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Sub Tab Navigation */}
          <div className="border-b border-gray-100">
            <nav className="flex space-x-4 px-6">
              {subTabs.map((subTab) => {
                const IconComponent = subTab.icon;
                return (
                  <button
                    key={subTab.id}
                    onClick={() => setActiveSubTab(subTab.id)}
                    className={`py-3 px-4 flex items-center space-x-2 font-medium text-sm rounded-t-lg ${
                      activeSubTab === subTab.id
                        ? 'bg-gray-50 text-gray-900 border-b-2 border-blue-500'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{subTab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Add New Content */}
            {activeSubTab === 'add' && (
              <>
                {activeTab === 'products' && (
                  <ProductForm onSuccess={handleSuccess} onError={handleError} />
                )}
                {activeTab === 'services' && (
                  <ServiceForm onSuccess={handleSuccess} onError={handleError} />
                )}
                {activeTab === 'references' && (
                  <ReferenceForm onSuccess={handleSuccess} onError={handleError} />
                )}
                {activeTab === 'news' && (
                  <NewsForm onSuccess={handleSuccess} onError={handleError} />
                )}
                {activeTab === 'gallery' && (
                  <GalleryForm onSuccess={handleSuccess} onError={handleError} />
                )}
                {activeTab === 'documents' && (
                  <DocumentForm onSuccess={handleSuccess} onError={handleError} />
                )}
              </>
            )}

            {/* Manage Existing Content */}
            {activeSubTab === 'manage' && (
              <ContentManager 
                contentType={activeTab}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel; 